<template>
  <div>
    <!-- modo "tela normal": selects de mês e ano -->
    <div v-if="!printMode" class="mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-auto">
          <label>Mês</label>
          <select v-model="mes" class="form-select">
            <option v-for="(lbl, i) in meses" :key="i" :value="i+1">
              {{ lbl }}
            </option>
          </select>
        </div>
        <div class="col-auto">
          <label>Ano</label>
          <input type="number" v-model.number="ano" class="form-control" min="2000" />
        </div>
        <div class="col-auto">
          <button class="btn btn-primary" @click="abrirPrint">Executar</button>
        </div>
      </div>
    </div>

    <!-- modo impressão: mostra tabelas de receitas, despesas e resultado econômico -->
    <div v-else>
      <h6 class="mb-3 text-center">{{ subtitle }}</h6>

      <!-- RECEITAS -->
      <div v-if="receitas.length">
        <h6>Receitas</h6>
        <table class="table table-striped mb-4">
          <thead>
            <tr>
              <th>Subconta</th>
              <th class="text-right">{{ subtitle }}</th>
              <th class="text-right">% Receitas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in receitas" :key="r.refil">
              <td>{{ r.refil }}</td>
              <td class="text-right">{{ formatarMoeda(r.valor) }}</td>
              <td class="text-right">{{ r.percentual.toFixed(2) }}%</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total Geral</strong></td>
              <td class="text-right">
                <strong>{{ formatarMoeda(totalReceitas) }}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- DESPESAS -->
      <div v-for="d in despesas" :key="d.plano.id" class="mb-4">
        <h6>{{ d.plano.nome }}</h6>
        <table class="table table-striped mb-4">
          <thead>
            <tr>
              <th>Subconta</th>
              <th class="text-right">{{ subtitle }}</th>
              <th class="text-right">DESPxDESP</th>
              <th class="text-right">DESPxREC</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in d.itens" :key="it.subconta">
              <td>{{ it.subconta }}</td>
              <td class="text-right">{{ formatarMoeda(it.valor) }}</td>
              <td class="text-right">{{ it.despxdesp.toFixed(2) }}%</td>
              <td class="text-right">{{ it.despxrec.toFixed(2) }}%</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Subtotal {{ d.plano.nome }}</strong></td>
              <td class="text-right">
                <strong>{{ formatarMoeda(d.totalPlano) }}</strong>
              </td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- TOTAL GERAL DE DESPESAS -->
      <!-- <div v-if="despesas.length" class="mb-4">
        <h6>Total Geral de Despesas</h6>
        <p><strong>{{ formatarMoeda(totalDespesas) }}</strong></p>
      </div> -->

            <!-- SUMÁRIO GERAL: Total Receitas x Total Despesas -->
      <div class="mt-4">
        <h6>Sumário Geral</h6>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Descrição</th>
              <th class="text-right">{{ subtitle }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Receitas</strong></td>
              <td class="text-right"><strong>{{ formatarMoeda(totalReceitas) }}</strong></td>
            </tr>
            <tr>
              <td><strong>Total Despesas</strong></td>
              <td class="text-right"><strong>{{ formatarMoeda(totalDespesas) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- RESULTADO ECONÔMICO -->
      <div>
        <h6>Resultado Econômico</h6>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-right">{{ subtitle }}</th>
              <th class="text-right">Lucratividade</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Resultado Econômico</strong></td>
              <td class="text-right"><strong>{{ formatarMoeda(resultadoEconomico) }}</strong></td>
              <td class="text-right"><strong>{{ percentualResultado.toFixed(2) }}%</strong></td>
            </tr>
          </tbody>
        </table>
      </div>


    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'

const props = defineProps({
  printMode: { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: String,
  subtitle:   String
})

const router = useRouter()
const meses = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ']
const mes     = ref(new Date().getMonth()+1)
const ano     = ref(new Date().getFullYear())

// dados do relatório
const receitas = ref([])
const totalReceitas = ref(0)
const despesas = ref([])
const totalDespesas = ref(0)

// cálculo do resultado econômico
const resultadoEconomico = computed(() => totalReceitas.value - totalDespesas.value)
const percentualResultado = computed(() =>
  totalReceitas.value > 0
    ? (resultadoEconomico.value / totalReceitas.value) * 100
    : 0
)

onMounted(async () => {
  if (!props.printMode) return
  const res = await axios.get('/gestao/relatorios/financeiros/dre', {
    params: { dataInicio: props.dataInicio, dataFim: props.dataFim }
  })
  receitas.value      = res.data.receitas
  totalReceitas.value = res.data.totalReceitas
  despesas.value      = res.data.despesas
  totalDespesas.value = res.data.totalDespesas
})

function abrirPrint() {
  const mm = String(mes.value).padStart(2,'0')
  const inicio = `${ano.value}-${mm}-01`
  const fim    = new Date(ano.value, mes.value, 0)
  const fimStr = `${ano.value}-${mm}-${String(fim.getDate()).padStart(2,'0')}`

  router.push({
    path: `/gestao/relatorios/print/dre`,
    query: {
      title:     'DRE Mensal',
      dataInicio: inicio,
      dataFim:    fimStr,
      subtitle:  `${meses[mes.value-1]}/${ano.value}`
    }
  })
}

function formatarMoeda(v) {
  return Number(v).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL'
  })
}
</script>

<style scoped>
.text-right { text-align: right !important; }
.mb-4      { margin-bottom: 1.5rem; }
</style>
